import Phaser from 'phaser';
import { AudioSystem } from '../systems/AudioSystem';
import { getToken, getUsername, isAuthenticated } from '../auth';
import { API_BASE } from '../api';

export interface MissionCompleteSceneData {
  timeTaken?: number;   // seconds
  troopsLost?: number;
  missionNumber?: number;
  levelId?: string;
  kills?: number;
}

export class MissionCompleteScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MissionCompleteScene' });
  }

  create(data?: MissionCompleteSceneData): void {
    const { width, height } = this.scale;
    const timeTaken = data?.timeTaken ?? 0;
    const troopsLost = data?.troopsLost ?? 0;
    const levelId = data?.levelId;
    const kills = data?.kills ?? 0;

    this.cameras.main.fadeIn(700, 0, 0, 0);

    // Play mission complete jingle
    try {
      const audio = new AudioSystem(this);
      audio.playMissionComplete();
    } catch (_) { /* AudioContext may not be available yet */ }

    // Dark overlay background
    this.add.rectangle(0, 0, width, height, 0x050f05).setOrigin(0, 0);

    // Golden top stripe
    this.add.rectangle(0, 0, width, 6, 0xffcc00).setOrigin(0, 0);
    this.add.rectangle(0, height - 6, width, 6, 0xffcc00).setOrigin(0, 0);

    // Stars decoration
    for (let i = 0; i < 3; i++) {
      this.add
        .text(width / 2 + (i - 1) * 40, height / 2 - 120, '★', {
          fontSize: '28px',
          color: '#ffcc00',
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        })
        .setOrigin(0.5, 0.5);
    }

    // Main title
    this.add
      .text(width / 2, height / 2 - 80, 'MISSION COMPLETE', {
        fontSize: '32px',
        color: '#ffcc00',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 5,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    // Stats
    const minutes = Math.floor(timeTaken / 60);
    const seconds = timeTaken % 60;
    const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    this.add
      .text(width / 2, height / 2, `Time: ${timeStr}`, {
        fontSize: '18px',
        color: '#aaffaa',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    this.add
      .text(width / 2, height / 2 + 32, `Troops lost: ${troopsLost}`, {
        fontSize: '18px',
        color: troopsLost === 0 ? '#00ff88' : '#ffaa44',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    this.add
      .text(width / 2, height / 2 + 64, `Kills: ${kills}`, {
        fontSize: '18px',
        color: '#aaffaa',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    // Score submission status (updated asynchronously)
    const scoreStatus = this.add
      .text(width / 2, height / 2 + 90, '', {
        fontSize: '11px',
        color: '#556677',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    if (levelId) {
      if (isAuthenticated()) {
        this.submitScore(levelId, timeTaken, kills, troopsLost, scoreStatus);
      } else {
        scoreStatus.setText('Sign in to save your score.');
        scoreStatus.setColor('#ffaa44');
        this.addAuthPrompt(
          width / 2,
          height / 2 + 110,
          levelId,
          timeTaken,
          kills,
          troopsLost,
          scoreStatus
        );
      }
    }

    // Rating UI — only shown for community levels (not campaign levels)
    const isCommunityLevel = levelId && levelId !== 'level1' && levelId !== 'level2';
    if (isCommunityLevel) {
      this.addRatingButtons(levelId, width / 2, height / 2 + 106);
    }

    // Buttons — pushed further down when auth prompt or rating UI is shown
    const btnY = isCommunityLevel ? height / 2 + 168 : height / 2 + 140;

    // If launched from the map editor, show a back-to-editor button instead of campaign buttons
    if (sessionStorage.getItem('ff_editor_test_mode') === '1') {
      sessionStorage.removeItem('ff_editor_test_mode');
      this.createButton(width / 2, btnY, '← BACK TO EDITOR', 0x00ff88, 0x003322, () => {
        this.cameras.main.fadeOut(500, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.wake('MapEditorScene');
          this.scene.stop('MissionCompleteScene');
        });
      });
      return;
    }

    this.createButton(width / 2 - 120, btnY, 'NEXT MISSION', 0xffcc00, 0x332200, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MemorialScene', { nextScene: 'BriefingScene' });
      });
    });

    this.createButton(width / 2 + 120, btnY, 'RETRY', 0x4488ff, 0x001133, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('GameScene');
      });
    });

    // Share button — captures the current scene as a PNG and triggers Web Share / download
    this.createButton(width / 2, btnY + 50, 'SHARE', 0x00ccff, 0x001122, () => {
      this.shareScreenshot();
    });

    // Score card button — generates an Instagram/X-ready composite image
    this.createButton(width / 2, btnY + 100, 'SCORE CARD', 0xff88cc, 0x220011, () => {
      this.shareScoreCard(timeTaken, kills, troopsLost);
    });
  }

  private shareScreenshot(): void {
    // Delay by one frame to ensure the current render is flushed to the GPU
    this.time.delayedCall(50, () => {
      this.game.renderer.snapshot((snapshot) => {
        if (!(snapshot instanceof HTMLImageElement)) return;
        const dataUrl = snapshot.src;
        this.doShare(dataUrl).catch(() => {
          // Silent failure — download fallback handles the error path
        });
      });
    });
  }

  private async doShare(dataUrl: string): Promise<void> {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], 'warboys-mission-complete.png', { type: 'image/png' });

    if (
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] })
    ) {
      // Native OS share sheet (iOS Safari, Android Chrome, macOS Safari 12+)
      await navigator.share({
        files: [file],
        title: 'Warboys',
        text: 'I just completed a mission! 🎮',
      });
    } else {
      // Desktop / unsupported — trigger a PNG download as fallback
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'warboys-mission-complete.png';
      a.click();
    }
  }

  private shareScoreCard(timeTaken: number, kills: number, troopsLost: number): void {
    this.time.delayedCall(50, () => {
      this.game.renderer.snapshot((snapshot) => {
        if (!(snapshot instanceof HTMLImageElement)) return;
        this.buildScoreCard(snapshot.src, timeTaken, kills, troopsLost)
          .then((dataUrl) => this.doShareScoreCard(dataUrl))
          .catch(() => { /* Silent failure */ });
      });
    });
  }

  private buildScoreCard(
    screenshotDataUrl: string,
    timeTaken: number,
    kills: number,
    troopsLost: number,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('No 2D context')); return; }

      const img = new Image();
      img.onload = () => {
        // Screenshot occupies the top 1080×1080 area (square crop)
        ctx.drawImage(img, 0, 0, 1080, 1080);

        // Dark stat panel below the screenshot
        ctx.fillStyle = '#0d1b2a';
        ctx.fillRect(0, 1080, 1080, 840);

        // Top divider line
        ctx.fillStyle = '#00ff88';
        ctx.fillRect(0, 1080, 1080, 4);

        // Stats text
        const minutes = Math.floor(timeTaken / 60);
        const seconds = timeTaken % 60;
        const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

        ctx.font = 'bold 72px monospace';
        ctx.fillStyle = '#ffcc00';
        ctx.textAlign = 'left';
        ctx.fillText('MISSION COMPLETE', 60, 1200);

        ctx.font = '56px monospace';
        ctx.fillStyle = '#00ff88';
        ctx.fillText(`Time:        ${timeStr}`, 60, 1320);
        ctx.fillText(`Kills:       ${kills}`, 60, 1400);
        ctx.fillStyle = troopsLost === 0 ? '#00ff88' : '#ffaa44';
        ctx.fillText(`Troops lost: ${troopsLost}`, 60, 1480);

        // "Can you beat me?" call-to-action
        ctx.font = 'bold 48px monospace';
        ctx.fillStyle = '#556677';
        ctx.textAlign = 'center';
        ctx.fillText('Can you beat me?', 540, 1640);

        // Warboys watermark
        ctx.font = 'bold 36px monospace';
        ctx.fillStyle = '#334455';
        ctx.textAlign = 'right';
        ctx.fillText('warboys.io', 1020, 1880);

        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = screenshotDataUrl;
    });
  }

  private async doShareScoreCard(dataUrl: string): Promise<void> {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], 'warboys-score-card.png', { type: 'image/png' });

    if (
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({ files: [file] })
    ) {
      await navigator.share({
        files: [file],
        title: 'Warboys',
        text: 'Can you beat my score? 🎮',
      });
    } else {
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = 'warboys-score-card.png';
      a.click();
    }
  }

  private addRatingButtons(levelId: string, x: number, y: number): void {
    const label = this.add
      .text(x, y, 'Rate this level:', {
        fontSize: '13px',
        color: '#556677',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    const statusText = this.add
      .text(x, y + 30, '', {
        fontSize: '12px',
        color: '#00ff88',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    const goodBtn = this.add
      .text(x - 50, y + 30, '[ GOOD ]', {
        fontSize: '13px',
        color: '#00ff88',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });

    const badBtn = this.add
      .text(x + 50, y + 30, '[ BAD ]', {
        fontSize: '13px',
        color: '#ff4444',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true });

    const submitRating = (rating: 1 | -1) => {
      goodBtn.destroy();
      badBtn.destroy();
      label.setText('');
      statusText.setText('Submitting…');
      this.submitRating(levelId, rating, statusText);
    };

    goodBtn.on('pointerdown', () => submitRating(1));
    badBtn.on('pointerdown', () => submitRating(-1));
  }

  private submitRating(levelId: string, rating: 1 | -1, statusText: Phaser.GameObjects.Text): void {
    fetch(`${API_BASE}/v1/levels/${encodeURIComponent(levelId)}/rating`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating }),
    })
      .then((res) => {
        statusText.setText(res.ok ? 'Rating saved.' : '');
      })
      .catch(() => {
        statusText.setText('');
      });
  }

  private submitScore(
    levelId: string,
    completionTimeSecs: number,
    kills: number,
    casualties: number,
    statusText: Phaser.GameObjects.Text
  ): void {
    const token = getToken();
    if (!token) {
      statusText.setText('Sign in to save your score.');
      statusText.setColor('#ffaa44');
      return;
    }

    const payload = {
      levelId,
      completionTimeSecs,
      kills,
      casualties,
      submittedAt: new Date().toISOString(),
    };

    fetch(`${API_BASE}/v1/levels/${encodeURIComponent(levelId)}/scores`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) {
          statusText.setText('Score saved.');
          statusText.setColor('#00ff88');
        }
        // Non-2xx: stay silent — don't block the player
      })
      .catch(() => {
        // Network failure: stay silent — don't block the player
      });
  }

  private addAuthPrompt(
    x: number,
    y: number,
    _levelId: string,
    _completionTimeSecs: number,
    _kills: number,
    _casualties: number,
    _scoreStatus: Phaser.GameObjects.Text
  ): void {
    const hasAccount = getUsername() !== null;
    const authUrl = hasAccount ? '/pages/auth.html#login' : '/pages/auth.html#register';
    const label = hasAccount ? '[ SIGN IN TO SUBMIT ]' : '[ REGISTER TO SUBMIT ]';
    const color = hasAccount ? '#4488ff' : '#00ff88';

    this.add
      .text(x, y + 22, label, {
        fontSize: '13px',
        color,
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        window.location.href = authUrl;
      });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    textColor: number,
    bgColor: number,
    onClick: () => void
  ): void {
    const btnWidth = 180;
    const btnHeight = 44;

    const bg = this.add
      .rectangle(x, y, btnWidth, btnHeight, bgColor)
      .setStrokeStyle(2, textColor)
      .setInteractive({ useHandCursor: true });

    const text = this.add
      .text(x, y, label, {
        fontSize: '15px',
        color: `#${textColor.toString(16).padStart(6, '0')}`,
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    bg.on('pointerover', () => {
      bg.setFillStyle(textColor);
      text.setColor(`#${bgColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(bgColor);
      text.setColor(`#${textColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerdown', onClick);
  }
}
